import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';
import '../../App.css';
import styles from '../SignUp.module.css';
import Footer from '../Footer';

// Add a countries array for the dropdown
const countries = [
  "United Kingdom", "United States", "Australia", "Canada", "France", "Germany", "India", "Ireland", "Italy", "Netherlands", "New Zealand", "Norway", "Poland", "Portugal", "South Africa", "Spain", "Sweden", "Switzerland", "Other"
];

const PORT = process.env.PORT || 5001;

export default function SignUp() {
  const { instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [mode, setMode] = useState('login'); // 'login' or 'register'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [country, setCountry] = useState('');
  const [city, setCity] = useState('');
  const [emailPlaceholder, setEmailPlaceholder] = useState('');
  const [passwordPlaceholder, setPasswordPlaceholder] = useState('');
  const [cursorVisible, setCursorVisible] = useState(true);
  const [message, setMessage] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const history = useHistory();

  // Typewriter effect for placeholders (only in login mode)
  useEffect(() => {
    if (mode !== 'login') {
      setEmailPlaceholder('Email Address');
      setPasswordPlaceholder('Password');
      return;
    }
    const emailText = 'Email Address';
    const passwordText = 'Password';
    let emailIndex = 0;
    let passwordIndex = 0;
    setEmailPlaceholder('');
    setPasswordPlaceholder('');
    const typingInterval = setInterval(() => {
      if (emailIndex < emailText.length) {
        const emailChar = emailText[emailIndex];
        setEmailPlaceholder((prev) => prev + emailChar);
        emailIndex++;
      }
      if (passwordIndex < passwordText.length) {
        const passwordChar = passwordText[passwordIndex];
        setPasswordPlaceholder((prev) => prev + passwordChar);
        passwordIndex++;
      }
      if (emailIndex >= emailText.length && passwordIndex >= passwordText.length) {
        clearInterval(typingInterval);
      }
    }, 150);
    return () => clearInterval(typingInterval);
  }, [mode]);

  // Blinking cursor effect (only in login mode)
  useEffect(() => {
    if (mode !== 'login') {
      setCursorVisible(false);
      return;
    }
    const blinkInterval = setInterval(() => {
      setCursorVisible(prev => !prev);
    }, 500);
    return () => clearInterval(blinkInterval);
  }, [mode]);

  // Placeholder with blinking cursor (only in login mode)
  const emailPH =
    mode === 'login'
      ? email.length === 0
        ? `${emailPlaceholder}${cursorVisible ? '|' : ''}`
        : ''
      : 'Email';

  const passwordPH =
    mode === 'login'
      ? password.length === 0
        ? `${passwordPlaceholder}${cursorVisible ? '|' : ''}`
        : ''
      : 'Password';

  // Handlers
  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    if (mode === 'register' && password !== confirmPassword) {
      setMessage('Passwords do not match');
      return;
    }
    const endpoint = mode === 'login' ? '/api/login' : '/api/register';
    const payload =
      mode === 'login'
        ? { email, password }
        : { email, password, confirmPassword, dateOfBirth, country, city };
    try {
      const res = await fetch(`http://localhost:${PORT}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (res.ok) {
        setMessage(mode === 'login' ? 'Login successful!' : 'Registration successful! You can now log in.');
        if (mode === 'login') {
          localStorage.setItem('token', data.token);
          window.dispatchEvent(new Event('authChange')); // <-- Add this line
          history.push('/notes');
        }
        setEmail('');
        setPassword('');
        setConfirmPassword('');
        setDateOfBirth('');
        setCountry('');
        setCity('');
      } else {
        setMessage(data.message || (mode === 'login' ? 'Login failed' : 'Registration failed'));
      }
    } catch (err) {
      setMessage('Server error');
    }
  };

  const handleModeSwitch = () => {
    setMode(mode === 'login' ? 'register' : 'login');
    setMessage('');
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setDateOfBirth('');
    setCountry('');
    setCity('');
  };

  return (
    <>
      <div className={styles.signupContainer}>
        <form className={styles.signupForm} onSubmit={handleSubmit}>
          <h2 className={styles.formTitle}>{mode === 'login' ? 'Login' : 'Register'}</h2>
          {/* Email */}
          <div className={styles.formGroup}>
            {mode === 'login' ? (
              <input
                type="email"
                className={styles.signupInput}
                placeholder={emailPH}
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                autoComplete="username"
              />
            ) : (
              <>
                <label htmlFor="reg-email" className={styles.signupLabel}>Email</label>
                <input
                  id="reg-email"
                  type="email"
                  className={styles.signupInput}
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  autoComplete="username"
                />
              </>
            )}
          </div>
          {/* Password */}
          <div className={styles.formGroup}>
            {mode === 'login' ? (
              <input
                type="password"
                className={styles.signupInput}
                placeholder={passwordPH}
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
            ) : (
              <>
                <label htmlFor="reg-password" className={styles.signupLabel}>Password</label>
                <input
                  id="reg-password"
                  type="password"
                  className={styles.signupInput}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                />
              </>
            )}
          </div>
          {/* Confirm Password */}
          {mode === 'register' && (
            <div className={styles.formGroup}>
              <label htmlFor="confirm-password" className={styles.signupLabel}>Confirm Password</label>
              <input
                id="confirm-password"
                type="password"
                className={styles.signupInput}
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
              />
            </div>
          )}
          {/* Date of Birth */}
          {mode === 'register' && (
            <div className={styles.formGroup}>
              <label htmlFor="dob" className={styles.signupLabel}>Date of Birth</label>
              <input
                id="dob"
                type="date"
                className={styles.signupInput}
                value={dateOfBirth}
                onChange={e => setDateOfBirth(e.target.value)}
                required
                max={new Date().toISOString().split('T')[0]}
              />
            </div>
          )}
          {/* Country */}
          {mode === 'register' && (
            <div className={styles.formGroup}>
              <label htmlFor="country" className={styles.signupLabel}>Country</label>
              <select
                id="country"
                className={styles.signupInput}
                value={country}
                onChange={e => setCountry(e.target.value)}
                required
              >
                <option value="" disabled>Select your country</option>
                {countries.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          )}
          {/* Town/City */}
          {mode === 'register' && (
            <div className={styles.formGroup}>
              <label htmlFor="city" className={styles.signupLabel}>Town/City</label>
              <input
                id="city"
                type="text"
                className={styles.signupInput}
                value={city}
                onChange={e => setCity(e.target.value)}
                required
              />
            </div>
          )}
          <button type="submit" className={styles.signupButton}>
            {mode === 'login' ? 'Sign In' : 'Register'}
          </button>
          {message && <p>{message}</p>}
          {/* Microsoft SSO — login mode only */}
          {mode === 'login' && (
            <>
              <div className={styles.msoDivider}>
                <span>or</span>
              </div>
              {isAuthenticated ? (
                <p className={styles.msoSignedIn}>
                  <i className='fas fa-check-circle'></i> Signed in with Microsoft
                </p>
              ) : (
                <button
                  type='button'
                  className={styles.msoButton}
                  onClick={() => instance.loginRedirect(loginRequest)}
                >
                  <svg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 21 21' style={{ marginRight: '10px', flexShrink: 0 }}>
                    <rect x='1' y='1' width='9' height='9' fill='#f25022'/>
                    <rect x='11' y='1' width='9' height='9' fill='#7fba00'/>
                    <rect x='1' y='11' width='9' height='9' fill='#00a4ef'/>
                    <rect x='11' y='11' width='9' height='9' fill='#ffb900'/>
                  </svg>
                  Sign in with Microsoft
                </button>
              )}
            </>
          )}
        </form>
        <div className={styles.signupExtra}>
          {mode === 'login' ? (
            <button className={styles.createAccount} onClick={handleModeSwitch}>
              Create New Account
            </button>
          ) : (
            <button className={styles.createAccount} onClick={handleModeSwitch}>
              Already have an account? Sign In
            </button>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
}
